import {
  AriaToastQueue,
  type IconComponent,
  type TOAST_STATE_VALUES,
} from "@kazaar-platform/design-system";

import { RiCheckboxCircleFill, RiCloseCircleLine } from "@remixicon/react";
import { type ReactNode } from "react";
import { flushSync } from "react-dom";

export interface ToastProps {
  key?: string;
  state?: (typeof TOAST_STATE_VALUES)[number];
  title: string;
  icon?: IconComponent;
  isLoading?: boolean;
  fixWidth?: boolean;
  sideComponentProps?: {
    componentType: "button" | "link";
    children: ReactNode;
  };
}

const toastQueue = new AriaToastQueue<ToastProps>({
  wrapUpdate: (fn) => {
    const modalOpen = document.querySelector('[role="dialog"]');
    if ("startViewTransition" in document && !modalOpen) {
      try {
        document.startViewTransition(() => flushSync(fn));
      } catch (e) {
        console.warn("transition fail", e);
        fn();
      }
    } else {
      fn();
    }
  },
});

const getIcon = (state: (typeof TOAST_STATE_VALUES)[number] | undefined) => {
  if (state === "error") {
    return RiCloseCircleLine;
  }
  if (state === "success") {
    return RiCheckboxCircleFill;
  }
  return undefined;
};

const activeToastKeys = new Set<string>();

const add = (toast: ToastProps) => {
  const key = toast.key ?? crypto.randomUUID();

  if (toast.key && activeToastKeys.has(key)) {
    return;
  }

  if (toast.key) {
    activeToastKeys.add(key);
  }

  toastQueue.add(
    {
      key,
      title: toast.title,
      state: toast.state || "default",
      icon: toast.icon ?? getIcon(toast.state),
    },
    {
      timeout: 5000,
      onClose: () => activeToastKeys.delete(key),
    }
  );
};

export const useToasts = () => {
  return { queue: toastQueue, add };
};
